import { format, parseISO, isValid } from 'date-fns';

/**
 * Centralized date formatting utilities to avoid repeated timezone queries
 * Uses date-fns for consistent, efficient formatting without database calls
 */

// Pre-defined format patterns to avoid repeated parsing
const DATE_FORMATS = {
  SHORT: 'MMM d, yyyy',           // Jan 1, 2024
  LONG: 'MMMM d, yyyy',           // January 1, 2024  
  FULL: 'EEEE, MMMM d, yyyy',     // Monday, January 1, 2024
  ISO: 'yyyy-MM-dd',              // 2024-01-01
  MONTH_YEAR: 'MMM yyyy',         // Jan 2024
} as const;

/**
 * Format a date string or Date object using cached patterns
 * @param dateInput - Date string, Date object, or null/undefined
 * @param formatType - Predefined format type or custom format string
 * @returns Formatted date string or fallback text
 */
export const formatDate = (
  dateInput: string | Date | null | undefined, 
  formatType: keyof typeof DATE_FORMATS | string = 'SHORT'
): string => {
  if (!dateInput) return 'Not set';
  
  try {
    let dateObj: Date;
    
    if (typeof dateInput === 'string') {
      // Handle ISO strings efficiently
      dateObj = dateInput.includes('T') ? parseISO(dateInput) : new Date(dateInput);
    } else {
      dateObj = dateInput;
    }
    
    if (!isValid(dateObj)) {
      console.warn('Invalid date provided to formatDate:', dateInput);
      return 'Invalid date';
    }
    
    // Use predefined format or custom format string
    const formatPattern = DATE_FORMATS[formatType as keyof typeof DATE_FORMATS] || formatType;
    
    return format(dateObj, formatPattern);
  } catch (error) {
    console.error('Date formatting error:', error, 'Input:', dateInput);
    return 'Invalid date';
  }
};

/**
 * Format date for CSV export (optimized for performance)
 */
export const formatDateForCsv = (dateInput: string | Date | null | undefined): string => {
  return formatDate(dateInput, DATE_FORMATS.ISO);
};

/**
 * Format date for display in components
 */
export const formatDateForDisplay = (dateInput: string | Date | null | undefined): string => {
  return formatDate(dateInput, DATE_FORMATS.SHORT);
};

/**
 * Format date for detailed views
 */
export const formatDateLong = (dateInput: string | Date | null | undefined): string => {
  return formatDate(dateInput, DATE_FORMATS.LONG);
};

/**
 * Calculate next service date efficiently
 */
export const getNextServiceDate = (startDate: string | Date | null | undefined): string => {
  if (!startDate) return 'Not scheduled';
  
  try {
    const startDateObj = typeof startDate === 'string' ? parseISO(startDate) : startDate;
    if (!isValid(startDateObj)) return 'Invalid date';
    
    const nextDate = new Date(startDateObj);
    nextDate.setDate(startDateObj.getDate() + 7);
    
    return format(nextDate, DATE_FORMATS.LONG);
  } catch (error) {
    console.error('Error calculating next service date:', error);
    return 'Unable to calculate';
  }
};

/**
 * Format number for locale display (cached)
 */
const numberFormatter = new Intl.NumberFormat('en-US');
export const formatNumber = (value: number): string => {
  return numberFormatter.format(value);
};